import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { formatDateLocale } from '../../utils/dateFormat';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import {
  ArrowLeft, Settings, Clock, Shield, Zap,
  ChevronDown, ChevronUp, Save, Loader2, AlertTriangle, Info
} from 'lucide-react';
import api from '../../utils/api';

// Canonical numeric bound codes → i18n key. Backend semantics unchanged.
const BOUNDS = {
  confirm_after_hours:       { min: 0, max: 8760, key: 'confirmLabel' },
  release_after_hours:       { min: 0, max: 8760, key: 'releaseLabel' },
  max_auto_releases_per_day: { min: 1, max: 1000, key: 'dailyLabel' },
};

function validate(field, value, t) {
  const b = BOUNDS[field];
  if (!b) return null;
  const label = t(`provider.autoRelease.bounds.${b.key}`);
  const n = Number(value);
  if (isNaN(n) || !Number.isInteger(n)) return t('provider.autoRelease.validation.integer', { label });
  if (n < b.min) return t('provider.autoRelease.validation.min', { label, n: b.min });
  if (n > b.max) return t('provider.autoRelease.validation.max', { label, n: b.max });
  return null;
}

export default function ClinicAutoReleaseSettings() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [config, setConfig] = useState(null);
  const [source, setSource] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [platformDisabled, setPlatformDisabled] = useState(false);

  const [form, setForm] = useState({
    enabled: true,
    auto_confirm_enabled: true,
    auto_release_enabled: true,
    confirm_after_hours: 12,
    release_after_hours: 0,
    require_escrow_fully_funded: true,
    max_auto_releases_per_day: 100,
  });
  const [original, setOriginal] = useState(null);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/clinic/auto-release/config`);
      if (res.status === 401 || res.status === 403) {
        setError(t('provider.autoRelease.authRequired'));
        return;
      }
      const data = res.data;
      const c = data.auto_release_config || {};
      setConfig(c);
      setSource(data.source || 'system_defaults');
      const formData = {
        enabled: c.enabled ?? true,
        auto_confirm_enabled: c.auto_confirm_enabled ?? true,
        auto_release_enabled: c.auto_release_enabled ?? true,
        confirm_after_hours: c.confirm_after_hours ?? 12,
        release_after_hours: c.release_after_hours ?? 0,
        require_escrow_fully_funded: c.require_escrow_fully_funded ?? true,
        max_auto_releases_per_day: c.max_auto_releases_per_day ?? 100,
      };
      setForm(formData);
      setOriginal(formData);

      try {
        const healthRes = await api.get(`/admin/auto-release/health`);
        if (healthRes.ok) {
          const h = healthRes.data;
          setPlatformDisabled(!h.enabled);
        }
      } catch { /* ignore */ }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const hasChanges = useMemo(() => {
    if (!original) return false;
    return Object.keys(form).some(k => form[k] !== original[k]);
  }, [form, original]);

  const validationErrors = useMemo(() => {
    const errs = {};
    for (const field of Object.keys(BOUNDS)) {
      const err = validate(field, form[field], t);
      if (err) errs[field] = err;
    }
    return errs;
  }, [form, t]);

  const canSave = hasChanges && Object.keys(validationErrors).length === 0 && !platformDisabled;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const res = await api.post(`/clinic/auto-release/config`, form);
      if (res.status === 401) { toast.error(t('provider.autoRelease.toasts.sessionExpired')); return; }
      if (res.status === 403) { toast.error(t('provider.autoRelease.toasts.permissionDenied')); return; }
      if (res.status === 400) {
        const d = res.data;
        toast.error(d.detail || t('provider.autoRelease.toasts.validationError'));
        return;
      }
      const data = res.data;
      const c = data.auto_release_config || {};
      setConfig(c);
      setSource(data.source || 'clinic_custom');
      const formData = {
        enabled: c.enabled ?? true,
        auto_confirm_enabled: c.auto_confirm_enabled ?? true,
        auto_release_enabled: c.auto_release_enabled ?? true,
        confirm_after_hours: c.confirm_after_hours ?? 12,
        release_after_hours: c.release_after_hours ?? 0,
        require_escrow_fully_funded: c.require_escrow_fully_funded ?? true,
        max_auto_releases_per_day: c.max_auto_releases_per_day ?? 100,
      };
      setForm(formData);
      setOriginal(formData);
      toast.success(t('provider.autoRelease.toasts.saved'));
    } catch (e) {
      toast.error(t('provider.autoRelease.toasts.saveFailed', { message: e.message }));
    } finally {
      setSaving(false);
    }
  };

  const setField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="clinic-auto-release-page">
      <header className="bg-[#122033] text-white border-b border-white/10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className="text-white/70 hover:text-white hover:bg-white/10"
              onClick={() => navigate('/clinic/admin')} data-testid="back-to-clinic">
              <ArrowLeft className="h-4 w-4 mr-1" /> {t('provider.autoRelease.back')}
            </Button>
            <div className="h-5 w-px bg-white/20" />
            <h1 className="text-lg font-semibold tracking-tight font-[Manrope] flex items-center gap-2">
              <Settings className="h-4.5 w-4.5" /> {t('provider.autoRelease.title')}
            </h1>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-4">
        {platformDisabled && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-center gap-3" data-testid="platform-disabled-banner">
            <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-amber-800">{t('provider.autoRelease.platformDisabledTitle')}</p>
              <p className="text-xs text-amber-600">{t('provider.autoRelease.platformDisabledDesc')}</p>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700" data-testid="config-error">
            {error}
          </div>
        )}

        {loading ? (
          <div className="space-y-3" data-testid="config-loading">
            {[1,2,3].map(i => <div key={i} className="h-20 bg-slate-100 animate-pulse rounded-lg" />)}
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 flex-wrap" data-testid="effective-config-info">
              <Badge variant="outline" className={`text-xs px-2 py-0.5 ${source === 'clinic_custom' ? 'border-emerald-200 text-emerald-700 bg-emerald-50' : 'border-slate-200 text-slate-500'}`}>
                {source === 'clinic_custom' ? t('provider.autoRelease.sourceCustom') : t('provider.autoRelease.sourceDefaults')}
              </Badge>
              {config?.version != null && (
                <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-slate-200 text-slate-400">
                  v{config.version}
                </Badge>
              )}
              {config?.updated_at_utc && (
                <span className="text-[10px] text-slate-400">
                  {t('provider.autoRelease.updatedPrefix', { date: formatDateLocale(config.updated_at_utc) })}
                </span>
              )}
            </div>

            <Card className="border border-slate-200/60 shadow-sm" data-testid="master-switch-card">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center">
                      <Zap className="h-4.5 w-4.5 text-emerald-600" />
                    </div>
                    <div>
                      <Label className="text-sm font-semibold text-[#122033] font-[Manrope]">{t('provider.autoRelease.master.title')}</Label>
                      <p className="text-xs text-slate-400">{t('provider.autoRelease.master.desc')}</p>
                    </div>
                  </div>
                  <Switch
                    checked={form.enabled}
                    onCheckedChange={(v) => setField('enabled', v)}
                    disabled={platformDisabled}
                    data-testid="master-switch"
                  />
                </div>
              </CardContent>
            </Card>

            <Card className="border border-slate-200/60 shadow-sm" data-testid="core-toggles-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-[#122033] font-[Manrope]">{t('provider.autoRelease.toggles.title')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm text-[#122033]">{t('provider.autoRelease.toggles.confirmTitle')}</Label>
                    <p className="text-xs text-slate-400">{t('provider.autoRelease.toggles.confirmDesc')}</p>
                  </div>
                  <Switch
                    checked={form.auto_confirm_enabled}
                    onCheckedChange={(v) => setField('auto_confirm_enabled', v)}
                    disabled={platformDisabled || !form.enabled}
                    data-testid="auto-confirm-switch"
                  />
                </div>
                <div className="border-t border-slate-100" />
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm text-[#122033]">{t('provider.autoRelease.toggles.releaseTitle')}</Label>
                    <p className="text-xs text-slate-400">{t('provider.autoRelease.toggles.releaseDesc')}</p>
                  </div>
                  <Switch
                    checked={form.auto_release_enabled}
                    onCheckedChange={(v) => setField('auto_release_enabled', v)}
                    disabled={platformDisabled || !form.enabled}
                    data-testid="auto-release-switch"
                  />
                </div>
              </CardContent>
            </Card>

            <Card className="border border-slate-200/60 shadow-sm" data-testid="delay-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-[#122033] font-[Manrope] flex items-center gap-2">
                  <Clock className="h-4 w-4 text-slate-400" /> {t('provider.autoRelease.timing.title')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-xs text-slate-500 uppercase tracking-wider mb-1 block">{t('provider.autoRelease.timing.confirmAfter')}</Label>
                  <Input
                    type="number" min={0} max={8760}
                    value={form.confirm_after_hours}
                    onChange={(e) => setField('confirm_after_hours', parseInt(e.target.value) || 0)}
                    disabled={platformDisabled || !form.enabled}
                    className="max-w-[200px]"
                    data-testid="confirm-hours-input"
                  />
                  {validationErrors.confirm_after_hours && (
                    <p className="text-xs text-red-500 mt-1" data-testid="confirm-hours-error">{validationErrors.confirm_after_hours}</p>
                  )}
                </div>
                <div>
                  <Label className="text-xs text-slate-500 uppercase tracking-wider mb-1 block">{t('provider.autoRelease.timing.releaseAfter')}</Label>
                  <Input
                    type="number" min={0} max={8760}
                    value={form.release_after_hours}
                    onChange={(e) => setField('release_after_hours', parseInt(e.target.value) || 0)}
                    disabled={platformDisabled || !form.enabled}
                    className="max-w-[200px]"
                    data-testid="release-hours-input"
                  />
                  {validationErrors.release_after_hours && (
                    <p className="text-xs text-red-500 mt-1" data-testid="release-hours-error">{validationErrors.release_after_hours}</p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="border border-slate-200/60 shadow-sm" data-testid="advanced-card">
              <button
                type="button"
                className="w-full flex items-center justify-between px-5 py-3 text-left"
                onClick={() => setShowAdvanced(!showAdvanced)}
                data-testid="advanced-toggle"
              >
                <span className="text-sm font-semibold text-[#122033] font-[Manrope] flex items-center gap-2">
                  <Shield className="h-4 w-4 text-slate-400" /> {t('provider.autoRelease.advanced.title')}
                </span>
                {showAdvanced ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
              </button>
              {showAdvanced && (
                <CardContent className="pt-0 space-y-4 border-t border-slate-100">
                  <div className="flex items-center justify-between pt-3">
                    <div>
                      <Label className="text-sm text-[#122033]">{t('provider.autoRelease.advanced.escrowTitle')}</Label>
                      <p className="text-xs text-slate-400">{t('provider.autoRelease.advanced.escrowDesc')}</p>
                    </div>
                    <Switch
                      checked={form.require_escrow_fully_funded}
                      onCheckedChange={(v) => setField('require_escrow_fully_funded', v)}
                      disabled={platformDisabled || !form.enabled}
                      data-testid="escrow-funded-switch"
                    />
                  </div>
                  <div className="border-t border-slate-100 pt-3">
                    <Label className="text-xs text-slate-500 uppercase tracking-wider mb-1 block">{t('provider.autoRelease.advanced.dailyCap')}</Label>
                    <Input
                      type="number" min={1} max={1000}
                      value={form.max_auto_releases_per_day}
                      onChange={(e) => setField('max_auto_releases_per_day', parseInt(e.target.value) || 1)}
                      disabled={platformDisabled || !form.enabled}
                      className="max-w-[200px]"
                      data-testid="daily-cap-input"
                    />
                    {validationErrors.max_auto_releases_per_day && (
                      <p className="text-xs text-red-500 mt-1" data-testid="daily-cap-error">{validationErrors.max_auto_releases_per_day}</p>
                    )}
                  </div>
                </CardContent>
              )}
            </Card>

            <div className="flex justify-end pt-2">
              <Button
                onClick={handleSave}
                disabled={!canSave || saving}
                className="bg-[#122033] hover:bg-[#1a2d45] text-white gap-2"
                data-testid="save-config-btn"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {saving ? t('provider.autoRelease.actions.saving') : t('provider.autoRelease.actions.save')}
              </Button>
            </div>

            <div className="flex items-start gap-2 text-[11px] text-slate-400 bg-slate-50 rounded-lg px-3 py-2" data-testid="info-note">
              <Info className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
              <p>{t('provider.autoRelease.info')}</p>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
